"use client";

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { updateAttachmentInCachedDrops, updateDropInCachedDrops } from "@/components/react-query-wrapper/utils/updateAttachmentInCachedDrops";
import type { ApiAttachment } from "@/generated/models/ApiAttachment";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiDropSearchStrategy } from "@/generated/models/ApiDropSearchStrategy";
import type { ApiDropType } from "@/generated/models/ApiDropType";
import { DropSize, type ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { WsDropUpdateMessage } from "@/helpers/Types";
import { WsMessageType } from "@/helpers/Types";
import { fetchWaveDropsFeedV2 } from "@/services/api/wave-drops-v2-api";
import { useWebSocketMessage } from "@/services/websocket/useWebSocketMessage";
import { useDebouncedQueryRefetch } from "./useDebouncedQueryRefetch";
const DEFAULT_WAVE_DROPS_LIMIT = 20;
interface UseWaveDropsProps {
  readonly waveId: string;
  readonly dropType?: ApiDropType | undefined;
  readonly containsMedia?: boolean | undefined;
  readonly curationId?: string | undefined;
  readonly limit?: number | undefined;
  readonly enabled?: boolean | undefined;
}
interface WaveDropsPage {
  readonly drops: ApiDrop[];
  readonly nextSerialNo?: number | undefined;
}
const hasMedia = (drop: ApiDrop): boolean => drop.parts.some(part => part.media.length > 0);
const processDrops = (pages: WaveDropsPage[] | undefined): ExtendedDrop[] => {
  if (!pages) {
    return [];
  }
  const allDrops = pages.flatMap(page => page.drops);
  const extendedDrops: ExtendedDrop[] = allDrops.map(drop => ({
    ...drop,
    type: DropSize.FULL,
    stableKey: drop.id,
    stableHash: drop.id
  }));
  const keyCount = new Map<string, number>();
  return extendedDrops.map(drop => {
    const count = (keyCount.get(drop.stableKey) ?? 0) + 1;
    keyCount.set(drop.stableKey, count);
    if (count > 1) {
      return {
        ...drop,
        stableKey: `${drop.stableKey}-${count}`
      };
    }
    return drop;
  });
};
export function useWaveDrops(t0) {
  const $ = _c(42);
  const {
    waveId,
    dropType,
    containsMedia: t1,
    curationId,
    limit: t2,
    enabled: t3
  } = t0;
  const containsMedia = t1 === undefined ? false : t1;
  const limit = t2 === undefined ? DEFAULT_WAVE_DROPS_LIMIT : t2;
  const enabled = t3 === undefined ? true : t3;
  const t4 = dropType ?? null;
  const t5 = curationId ?? null;
  let t6;
  if ($[0] !== containsMedia || $[1] !== limit || $[2] !== t4 || $[3] !== t5 || $[4] !== waveId) {
    t6 = [QueryKey.DROPS, {
      waveId,
      limit,
      dropType: t4,
      containsMedia,
      curationId: t5,
      context: "wave-drops"
    }];
    $[0] = containsMedia;
    $[1] = limit;
    $[2] = t4;
    $[3] = t5;
    $[4] = waveId;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const queryKey = t6 as const;
  const queryClient = useQueryClient();
  let t7;
  if ($[6] !== containsMedia || $[7] !== dropType || $[8] !== limit || $[9] !== waveId) {
    t7 = async t8 => {
      const {
        pageParam
      } = t8;
      const collectedDrops = [];
      let nextSerialNo;
      let serialNoLimit = pageParam;
      for (let shouldFetch = true; shouldFetch;) {
        const feed = await fetchWaveDropsFeedV2({
          waveId,
          limit,
          serialNoLimit,
          searchStrategy: typeof serialNoLimit === "number" ? ApiDropSearchStrategy.Older : undefined,
          dropType
        });
        const pageDrops = feed.drops as ApiDrop[];
        if (pageDrops.length === 0) {
          return {
            drops: collectedDrops
          };
        }
        nextSerialNo = pageDrops.at(-1)?.serial_no;
        collectedDrops.push(...(containsMedia ? pageDrops.filter(hasMedia) : pageDrops));
        if (!containsMedia || collectedDrops.length > 0 || nextSerialNo === undefined || nextSerialNo === serialNoLimit) {
          shouldFetch = false;
          continue;
        }
        serialNoLimit = nextSerialNo;
      }
      return {
        drops: collectedDrops,
        nextSerialNo
      };
    };
    $[6] = containsMedia;
    $[7] = dropType;
    $[8] = limit;
    $[9] = waveId;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = enabled && !!waveId;
  let t9;
  if ($[11] !== queryKey || $[12] !== t7 || $[13] !== t8) {
    t9 = {
      queryKey,
      queryFn: t7,
      enabled: t8,
      initialPageParam: null,
      getNextPageParam: _temp,
      placeholderData: keepPreviousData,
      staleTime: 60000,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true
    };
    $[11] = queryKey;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const {
    data,
    dataUpdatedAt,
    error,
    fetchNextPage: onFetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isPlaceholderData,
    refetch
  } = useInfiniteQuery(t9);
  let t10;
  if ($[15] !== hasNextPage || $[16] !== isFetchingNextPage || $[17] !== onFetchNextPage) {
    t10 = async () => {
      if (hasNextPage && !isFetchingNextPage) {
        await onFetchNextPage();
      }
    };
    $[15] = hasNextPage;
    $[16] = isFetchingNextPage;
    $[17] = onFetchNextPage;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const fetchNextPage = t10;
  data?.pages;
  const t11 = data?.pages;
  let t12;
  if ($[19] !== t11) {
    t12 = processDrops(t11);
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const drops = t12;
  let t13;
  if ($[21] !== isFetching || $[22] !== isFetchingNextPage || $[23] !== refetch) {
    t13 = {
      refetch,
      isFetching,
      isFetchingNextPage
    };
    $[21] = isFetching;
    $[22] = isFetchingNextPage;
    $[23] = refetch;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const requestRefetch = useDebouncedQueryRefetch(t13);
  let t14;
  if ($[25] !== queryClient || $[26] !== requestRefetch || $[27] !== waveId) {
    t14 = message => {
      if (waveId !== message.wave.id) {
        return;
      }
      updateDropInCachedDrops(queryClient, message);
      requestRefetch();
    };
    $[25] = queryClient;
    $[26] = requestRefetch;
    $[27] = waveId;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  useWebSocketMessage(WsMessageType.DROP_UPDATE, t14);
  let t15;
  if ($[29] !== queryClient) {
    t15 = attachment => {
      updateAttachmentInCachedDrops(queryClient, attachment);
    };
    $[29] = queryClient;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  useWebSocketMessage(WsMessageType.ATTACHMENT_STATUS_UPDATE, t15);
  let t16;
  if ($[31] !== dataUpdatedAt || $[32] !== drops || $[33] !== error || $[34] !== fetchNextPage || $[35] !== hasNextPage || $[36] !== isError || $[37] !== isFetching || $[38] !== isFetchingNextPage || $[39] !== isPlaceholderData || $[40] !== refetch) {
    t16 = {
      dataUpdatedAt,
      drops,
      error,
      fetchNextPage,
      hasNextPage,
      isError,
      isFetching,
      isFetchingNextPage,
      isPlaceholderData,
      refetch
    };
    $[31] = dataUpdatedAt;
    $[32] = drops;
    $[33] = error;
    $[34] = fetchNextPage;
    $[35] = hasNextPage;
    $[36] = isError;
    $[37] = isFetching;
    $[38] = isFetchingNextPage;
    $[39] = isPlaceholderData;
    $[40] = refetch;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  return t16;
}
function _temp(lastPage) {
  return lastPage.nextSerialNo;
}